import { useState } from 'react';
import t from 'react-translate';
import moment from 'moment';

import {
  REQUEST_DATE_FORMAT,
  SingleMentorRequestStatus as RequestStatus,
} from 'redux/schemas/models/mentor-request';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { MentorshipProgramRequest } from 'redux/schemas/models/mentorship-program-connections';

import { css } from '@emotion/react';
import { menteeLightBackgroundColor } from 'athena/styles/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { success, gray2, gray7 } from 'styles/global_defaults/colors';
import { textLargeBodyFontSize } from 'styles/global_defaults/fonts';

import ClickableContainer from 'components/clickable-container';
import { useMentorRequestContext } from '../mentor-request-context';
import { config } from '@config/pendo.config.json';

const requestStatuses = {
  [RequestStatus.PENDING]: {
    borderColor: success,
    backgroundColor: menteeLightBackgroundColor,
  },
  [RequestStatus.REJECTED]: {
    borderColor: gray2,
    backgroundColor: gray7,
  },
};

const requestSentStyles = (state: RequestStatus) => css`
  background-color: ${(
    requestStatuses[state]
    || requestStatuses[RequestStatus.REJECTED]
  ).backgroundColor};
  border-left: 2.5px solid ${(
    requestStatuses[state]
    || requestStatuses[RequestStatus.REJECTED]
  ).borderColor};
  border-radius: ${quarterSpacing}px;
  .request-message {
    ${state === RequestStatus.PENDING ? 'font-style: italic;' : ''}
  }
  .froala-style-regular {
    font-size: ${textLargeBodyFontSize}px;
  }
`;

const MentorRequestSent = ({ request }: { request: MentorshipProgramRequest }) => {
  const { participant, role } = useMentorRequestContext();

  const { firstName } = participant.user;
  const {
    state,
    message: requestMessage,
    createdAt,
    mentorAnsweredDate,
  } = request;

  const isRequestClosed = state === RequestStatus.REJECTED;
  const isRequestPending = state === RequestStatus.PENDING;

  const [isExpanded, setIsExpanded] = useState(!isRequestClosed);
  const isMentor = role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR;
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL;
  const menteeTranslations = translationBase.MENTEE_VIEW;
  const mentorTranslations = translationBase.MENTOR_VIEW;

  const getActiveRequestTitle = () => (
    isMentor
      ? mentorTranslations.REQUEST_RECEIVED_TITLE()
      : menteeTranslations.REQUEST_SENT_TITLE()
  );

  const [title, message] = isRequestClosed
    ? [
      menteeTranslations.REQUEST_CLOSED_TITLE(),
      menteeTranslations.REQUEST_CLOSED_MESSAGE(firstName),
    ] : [
      getActiveRequestTitle(),
      requestMessage,
    ];

  const date = isRequestPending ? createdAt : mentorAnsweredDate;

  const dataQATag = (
    config.pendo.athena.mentorshipProgram.participantHome.mentorRequest.modal.requestSentItem
  );

  return (isRequestClosed || isRequestPending) && (
    <ClickableContainer
      css={requestSentStyles(state)}
      className='d-flex flex-column py-2 px-4 mb-3'
      onClick={() => setIsExpanded(!isExpanded)}
      data-qa={dataQATag}
      data-qa-id={`${dataQATag}-${request.id}`}
      aria-expanded={isExpanded}
    >
      <div className='d-flex pb-1 align-items-center'>
        <div className='flex-1 flex-grow-1 heading-5'>
          {title}
        </div>
        <div className='flex-grow-1 text-right gray-2'>
          {moment(date).format(REQUEST_DATE_FORMAT)}
        </div>
      </div>
      {isExpanded && (
        <div
          className='text-large-body gray-1 request-message'
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        />
      )}
    </ClickableContainer>
  );
};

export default MentorRequestSent;
